import React from "react";
import { motion } from "framer-motion";
import { Plus, X, Loader2 } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

// A single connect/disconnect row — the shared UX for all bindings
// (CRM access, Gmail, project links). Status dot + label + action button.
// `connected`, `loading`, `onConnect`, `onDisconnect`, optional `right` slot.
export function ConnectRow({ icon, title, subtitle, connected, loading, onConnect, onDisconnect, right, disabled }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: t.s[3],
      borderRadius: t.r.md, border: `1px solid ${t.c.border}`,
      background: connected ? t.status.positiveSoft : t.c.surfaceMuted,
      transition: "background 200ms",
    }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10,
        background: connected ? "rgba(255,255,255,0.6)" : t.c.surface, color: connected ? t.status.positive : t.c.textTertiary,
        flexShrink: 0, boxShadow: `inset 0 0 0 1px ${t.c.border}`,
      }}>{icon}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, display: "flex", alignItems: "center", gap: 7 }}>
          {title}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: t.font.size.xs, fontWeight: 600, color: connected ? t.status.positive : t.c.textTertiary }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: connected ? t.status.positive : t.c.textTertiary }} />
            {connected ? "Connected" : "Not connected"}
          </span>
        </div>
        {subtitle && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
      </div>

      {right}

      <motion.button
        whileTap={{ scale: 0.96 }} transition={t.motion.spring.snappy}
        onClick={connected ? onDisconnect : onConnect} disabled={loading || disabled}
        style={{
          height: 32, padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 6,
          borderRadius: t.r.sm, border: `1px solid ${connected ? t.c.border : "transparent"}`,
          background: connected ? t.c.surface : t.c.primary, color: connected ? t.c.textSecondary : "#fff",
          fontSize: t.font.size.sm, fontWeight: 500, fontFamily: t.font.family,
          cursor: loading || disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, flexShrink: 0,
          transition: "background 140ms",
        }}
      >
        {loading ? <Loader2 size={14} className="dashv2-spin" /> : connected ? <X size={14} /> : <Plus size={14} />}
        {loading ? "…" : connected ? "Disconnect" : "Connect"}
      </motion.button>
    </div>
  );
}

// Section label used inside drawers to group bindings.
export function DrawerSection({ title, children }) {
  const t = useDashTheme();
  return (
    <div>
      <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: t.s[3] }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>{children}</div>
    </div>
  );
}
